<template>
  <div ref="rootElement" class="pt-[2rem] pb-[5rem]">

    <!-- 상단 액션 -->
    <div class="flex items-center justify-end mb-8">
      <button
        @click="handleReset"
        class="btn-secondary w-1/6 font-semibold text-green-400"
      >
        <i class="bi bi-arrow-repeat mr-2"></i>
        다시 분석하기
      </button>
    </div>

    <div class="space-y-48 mb-24">
      <!-- 섹션 1: 투자대상기업 정보 -->
      <section class="space-y-8">
        <div class="flex items-center gap-3 text-white">
          <i class="bi bi-1-square text-2xl"></i>
          <h4 class="text-heading-2 font-semibold">투자대상기업 정보</h4>
        </div>
  
        <div class="bg-dark-700 border border-dark-600 rounded-2xl p-8 shadow-[0_20px_45px_rgba(0,0,0,0.35)] space-y-8">
          <div class="flex flex-wrap items-center gap-4 bg-dark-800/70 border border-primary/30 rounded-xl px-6 py-4">
            <p class="text-caption uppercase tracking-[0.2em] text-primary">투자대상기업명</p>
            <h3 class="text-heading-4 text-white font-semibold">{{ companyInfo.company_name || '-' }}</h3>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-2">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-calendar-event text-green-400 text-lg"></i>
                설립일
              </div>
              <p class="text-white text-base">{{ companyInfo.founded_year || '-' }}</p>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-2">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-graph-up-arrow text-primary text-lg"></i>
                매출액
              </div>
              <p class="text-white text-base break-words whitespace-normal">{{ companyInfo.revenue || '-' }}</p>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-2">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-clock-history text-cyan-400 text-lg"></i>
                사업 경력 및 개요
              </div>
              <p class="text-white text-base leading-relaxed break-words whitespace-normal">{{ companyInfo.business_history || '-' }}</p>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-3">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-briefcase text-purple-400 text-lg"></i>
                사업 분야
              </div>
              <div class="text-white text-base leading-relaxed space-y-2">
                <p
                  v-for="(field, index) in companyInfo.business_fields || []"
                  :key="index"
                  class="flex items-start gap-2 break-words"
                >
                  <i class="bi bi-dot text-white flex-shrink-0 mt-0.5"></i>
                  <span class="break-words whitespace-normal">{{ field }}</span>
                </p>
                <p v-if="!companyInfo.business_fields || companyInfo.business_fields.length === 0" class="text-gray-500 text-base">-</p>
              </div>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-3">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-box-seam text-yellow-400 text-lg"></i>
                주요 제품
              </div>
              <div class="text-white text-base leading-relaxed space-y-2">
                <p
                  v-for="(product, index) in companyInfo.main_products || []"
                  :key="index"
                  class="flex items-start gap-2 break-words"
                >
                  <i class="bi bi-dot text-white flex-shrink-0 mt-0.5"></i>
                  <span class="break-words whitespace-normal">{{ product }}</span>
                </p>
                <p v-if="!companyInfo.main_products || companyInfo.main_products.length === 0" class="text-gray-500 text-base">-</p>
              </div>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-3">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-star text-yellow-400 text-lg"></i>
                주요 강점
              </div>
              <div class="text-white text-base leading-relaxed space-y-2">
                <p
                  v-for="(strength, index) in companyInfo.key_strengths || []"
                  :key="index"
                  class="flex items-start gap-2 break-words"
                >
                  <i class="bi bi-dot text-white flex-shrink-0 mt-0.5"></i>
                  <span class="break-words whitespace-normal">{{ strength }}</span>
                </p>
                <p v-if="!companyInfo.key_strengths || companyInfo.key_strengths.length === 0" class="text-gray-500 text-base">-</p>
              </div>
            </div>

            <div class="bg-dark-800/60 border border-dark-600 rounded-2xl p-5 space-y-2 lg:col-span-2">
              <div class="flex items-center gap-2 text-caption text-gray-400 uppercase tracking-[0.2em]">
                <i class="bi bi-trophy text-orange-400 text-lg"></i>
                시장 지위
              </div>
              <p class="text-white text-base leading-relaxed break-words whitespace-normal">{{ companyInfo.market_position || '-' }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 섹션 2: 리스크 대시보드 -->
      <section>
        <div class="flex items-center gap-3 text-white mb-8">
          <i class="bi bi-2-square text-2xl"></i>
          <h4 class="text-heading-2 font-semibold">리스크 분석 요약 정보</h4>
        </div>

        <div class="bg-dark-700/50 border border-dark-600 rounded-2xl p-8">
          <!-- 첫 번째 행: 카테고리별 리스크 요약 (3개 가로 배치) -->
          <div class="space-y-6">
            <h5 class="text-white text-lg font-semibold flex items-center gap-2">
              <i class="bi bi-list-ul text-primary"></i>
              카테고리별 리스크 요약
            </h5>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-10">
              <!-- 사업 리스크 -->
              <div class="bg-dark-700 border border-dark-600 rounded-2xl p-6 space-y-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                      <i class="bi bi-briefcase text-red-400 text-xl"></i>
                    </div>
                    <h5 class="text-white text-lg font-semibold">사업 리스크</h5>
                  </div>
                  <span v-if="businessRisks.length > 0" class="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 text-sm font-semibold">
                    {{ businessRisks.length }}개
                  </span>
                </div>
                <div v-if="businessRisks.length > 0" class="space-y-2.5 max-h-64 overflow-y-auto">
                  <div
                    v-for="(risk, index) in businessRisks"
                    :key="`business-${index}`"
                    class="flex items-start gap-3 p-3 rounded-lg bg-dark-800/60 border border-dark-600/50 hover:bg-dark-800/80 hover:border-red-500/30 transition-all duration-200"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-0.5">
                      <span class="text-red-400 text-xs font-semibold">{{ index + 1 }}</span>
                    </div>
                    <p class="text-gray-200 text-base leading-relaxed break-words flex-1">{{ risk }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-500 text-base italic">사업 리스크 정보가 없습니다.</p>
              </div>

              <!-- 시장 리스크 -->
              <div class="bg-dark-700 border border-dark-600 rounded-2xl p-6 space-y-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <i class="bi bi-graph-up text-yellow-400 text-xl"></i>
                    </div>
                    <h5 class="text-white text-lg font-semibold">시장 리스크</h5>
                  </div>
                  <span v-if="marketRisks.length > 0" class="px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-sm font-semibold">
                    {{ marketRisks.length }}개
                  </span>
                </div>
                <div v-if="marketRisks.length > 0" class="space-y-2.5 max-h-64 overflow-y-auto">
                  <div
                    v-for="(risk, index) in marketRisks"
                    :key="`market-${index}`"
                    class="flex items-start gap-3 p-3 rounded-lg bg-dark-800/60 border border-dark-600/50 hover:bg-dark-800/80 hover:border-yellow-500/30 transition-all duration-200"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <span class="text-yellow-400 text-xs font-semibold">{{ index + 1 }}</span>
                    </div>
                    <p class="text-gray-200 text-base leading-relaxed break-words flex-1">{{ risk }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-500 text-base italic">시장 리스크 정보가 없습니다.</p>
              </div>

              <!-- 운영 리스크 -->
              <div class="bg-dark-700 border border-dark-600 rounded-2xl p-6 space-y-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <i class="bi bi-gear text-emerald-400 text-xl"></i>
                    </div>
                    <h5 class="text-white text-lg font-semibold">운영 리스크</h5>
                  </div>
                  <span v-if="operationalRisks.length > 0" class="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-sm font-semibold">
                    {{ operationalRisks.length }}개
                  </span>
                </div>
                <div v-if="operationalRisks.length > 0" class="space-y-2.5 max-h-64 overflow-y-auto">
                  <div
                    v-for="(risk, index) in operationalRisks"
                    :key="`operational-${index}`"
                    class="flex items-start gap-3 p-3 rounded-lg bg-dark-800/60 border border-dark-600/50 hover:bg-dark-800/80 hover:border-emerald-500/30 transition-all duration-200"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center mt-0.5">
                      <span class="text-emerald-400 text-xs font-semibold">{{ index + 1 }}</span>
                    </div>
                    <p class="text-gray-200 text-base leading-relaxed break-words flex-1">{{ risk }}</p>
                  </div>
                </div>
                <p v-else class="text-gray-500 text-base italic">운영 리스크 정보가 없습니다.</p>
              </div>
            </div>
          </div>

          <!-- 두 번째 행: 심각도별 리스크 분포 -->
          <div class="space-y-6 mt-20">
            <h5 class="text-white text-lg font-semibold flex items-center gap-2">
              <i class="bi bi-bar-chart text-primary"></i>
              심각도별 리스크 분포
            </h5>
            
            <div class="bg-dark-700 border border-dark-600 rounded-2xl p-8 shadow-[0_12px_30px_rgba(0,0,0,0.35)]">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-14">
                <!-- 심각도별 합계 테이블 -->
                <div class="flex flex-col">
                  <h6 class="text-gray-300 text-base font-semibold mb-8">
                    <i class="bi bi-check-square text-gray-300 mr-2"></i>
                    심각도별 합계
                  </h6>
                  <div class="flex-1 overflow-x-auto">
                    <table class="w-full border-collapse h-full">
                      <thead>
                        <tr class="border-b-2 border-dark-600">
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold w-[50%]">
                            리스크 심각도
                          </th>
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                            합계
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="severity in ['높음', '중간', '낮음']"
                          :key="severity"
                          :class="[
                            'border-b border-dark-600/50 transition-colors',
                            severity === '높음' ? 'bg-red-500/5 hover:bg-red-500/10' :
                            severity === '중간' ? 'bg-yellow-500/5 hover:bg-yellow-500/10' :
                            'bg-emerald-500/5 hover:bg-emerald-500/10'
                          ]"
                        >
                          <td class="py-4 px-6 font-semibold text-base text-center">
                            <span class="flex items-center justify-center gap-2"
                              :class="[
                                severity === '높음' ? 'text-red-400' :
                                severity === '중간' ? 'text-yellow-400' :
                                'text-emerald-400'
                              ]"
                            >
                              {{ severity }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-white font-semibold text-2xl">
                              {{ riskMatrix['사업'][severity] + riskMatrix['시장'][severity] + riskMatrix['운영'][severity] }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- 카테고리별 상세 분포 -->
                <div class="flex flex-col">
                  <h6 class="text-gray-300 text-base font-semibold mb-8">
                    <i class="bi bi-check-square text-gray-300 mr-2"></i>
                    카테고리별 분포
                  </h6>
                  <div class="flex-1 overflow-x-auto">
                    <table class="w-full border-collapse h-full">
                      <thead>
                        <tr class="border-b-2 border-dark-600">
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                            카테고리
                          </th>
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                            리스크 높음
                          </th>
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                            리스크 중간
                          </th>
                          <th class="text-center py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                            리스크 낮음
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          class="border-b border-dark-600/50 bg-red-500/5 hover:bg-red-500/10 transition-colors"
                        >
                          <td class="py-4 px-6 font-semibold text-base text-center">
                            <div class="flex items-center justify-center gap-2">
                              <div class="w-3 h-3 rounded-full bg-red-500/40"></div>
                              <span class="text-gray-200">사업 리스크</span>
                            </div>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-red-400 font-semibold text-2xl">
                              {{ riskMatrix['사업']['높음'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-yellow-400 font-semibold text-2xl">
                              {{ riskMatrix['사업']['중간'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-emerald-400 font-semibold text-2xl">
                              {{ riskMatrix['사업']['낮음'] }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          class="border-b border-dark-600/50 bg-yellow-500/5 hover:bg-yellow-500/10 transition-colors"
                        >
                          <td class="py-4 px-6 font-semibold text-base text-center">
                            <div class="flex items-center justify-center gap-2">
                              <div class="w-3 h-3 rounded-full bg-yellow-500/40"></div>
                              <span class="text-gray-200">시장 리스크</span>
                            </div>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-red-400 font-semibold text-2xl">
                              {{ riskMatrix['시장']['높음'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-yellow-400 font-semibold text-2xl">
                              {{ riskMatrix['시장']['중간'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-emerald-400 font-semibold text-2xl">
                              {{ riskMatrix['시장']['낮음'] }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          class="border-b border-dark-600/50 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
                        >
                          <td class="py-4 px-6 font-semibold text-base text-center">
                            <div class="flex items-center justify-center gap-2">
                              <div class="w-3 h-3 rounded-full bg-emerald-500/40"></div>
                              <span class="text-gray-200">운영 리스크</span>
                            </div>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-red-400 font-semibold text-2xl">
                              {{ riskMatrix['운영']['높음'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-yellow-400 font-semibold text-2xl">
                              {{ riskMatrix['운영']['중간'] }}
                            </span>
                          </td>
                          <td class="py-4 px-6 text-center">
                            <span class="text-emerald-400 font-semibold text-2xl">
                              {{ riskMatrix['운영']['낮음'] }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 섹션 3: 카테고리별 리스크 상세 테이블 -->
      <section class="space-y-8">
        <div class="flex items-center gap-5 text-white">
          <i class="bi bi-3-square text-2xl"></i>
          <h4 class="text-heading-2 font-semibold">카테고리별 리스크 상세 정보</h4>
        </div>

        <div class="space-y-8">
          <!-- 사업 리스크 테이블 -->
          <div v-if="categorizedBusinessRisks.length > 0" class="bg-dark-700 border border-dark-600 rounded-2xl p-8 space-y-6">
            <details class="group">
              <summary class="cursor-pointer list-none flex items-center justify-between hover:opacity-80 transition-opacity">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <i class="bi bi-briefcase text-red-400 text-xl"></i>
                  </div>
                  <h5 class="text-white text-lg font-semibold">사업 리스크</h5>
                  <span class="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 text-sm font-semibold">
                    {{ categorizedBusinessRisks.length }}개
                  </span>
                </div>
                <div class="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 text-sm font-semibold flex items-center gap-2 pointer-events-none">
                  <span class="group-open:hidden">자세히보기</span>
                  <span class="hidden group-open:inline">접기</span>
                  <i class="bi bi-chevron-down group-open:rotate-180 transition-transform duration-200"></i>
                </div>
              </summary>
              
              <div class="mt-6 overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="border-b-2 border-dark-600">
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold w-[40%]">
                        리스크 요소
                      </th>
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                        리스크 설명
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(risk, index) in categorizedBusinessRisks"
                      :key="`business-${index}`"
                      class="border-b border-dark-600/50 hover:bg-dark-800/50 transition-colors"
                    >
                      <td class="py-4 px-6 text-white font-semibold text-base break-words whitespace-normal">
                        {{ risk.risk_name }}
                      </td>
                      <td class="py-4 px-6 text-gray-300 text-base leading-relaxed break-words whitespace-normal">
                        {{ risk.risk_description }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <!-- 시장 리스크 테이블 -->
          <div v-if="categorizedMarketRisks.length > 0" class="bg-dark-700 border border-dark-600 rounded-2xl p-8 space-y-6">
            <details class="group">
              <summary class="cursor-pointer list-none flex items-center justify-between hover:opacity-80 transition-opacity">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <i class="bi bi-graph-up text-yellow-400 text-xl"></i>
                  </div>
                  <h5 class="text-white text-lg font-semibold">시장 리스크</h5>
                  <span class="px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-sm font-semibold">
                    {{ categorizedMarketRisks.length }}개
                  </span>
                </div>
                <div class="px-4 py-2 rounded-lg bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-sm font-semibold flex items-center gap-2 pointer-events-none">
                  <span class="group-open:hidden">자세히보기</span>
                  <span class="hidden group-open:inline">접기</span>
                  <i class="bi bi-chevron-down group-open:rotate-180 transition-transform duration-200"></i>
                </div>
              </summary>
              
              <div class="mt-6 overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="border-b-2 border-dark-600">
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold w-[40%]">
                        리스크 요소
                      </th>
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                        리스크 설명
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(risk, index) in categorizedMarketRisks"
                      :key="`market-${index}`"
                      class="border-b border-dark-600/50 hover:bg-dark-800/50 transition-colors"
                    >
                      <td class="py-4 px-6 text-white font-semibold text-base break-words whitespace-normal">
                        {{ risk.risk_name }}
                      </td>
                      <td class="py-4 px-6 text-gray-300 text-base leading-relaxed break-words whitespace-normal">
                        {{ risk.risk_description }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <!-- 운영 리스크 테이블 -->
          <div v-if="categorizedOperationalRisks.length > 0" class="bg-dark-700 border border-dark-600 rounded-2xl p-8 space-y-6">
            <details class="group">
              <summary class="cursor-pointer list-none flex items-center justify-between hover:opacity-80 transition-opacity">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <i class="bi bi-gear text-emerald-400 text-xl"></i>
                  </div>
                  <h5 class="text-white text-lg font-semibold">운영 리스크</h5>
                  <span class="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-sm font-semibold">
                    {{ categorizedOperationalRisks.length }}개
                  </span>
                </div>
                <div class="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-sm font-semibold flex items-center gap-2 pointer-events-none">
                  <span class="group-open:hidden">자세히보기</span>
                  <span class="hidden group-open:inline">접기</span>
                  <i class="bi bi-chevron-down group-open:rotate-180 transition-transform duration-200"></i>
                </div>
              </summary>
              
              <div class="mt-6 overflow-x-auto">
                <table class="w-full border-collapse">
                  <thead>
                    <tr class="border-b-2 border-dark-600">
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold w-[40%]">
                        리스크 요소
                      </th>
                      <th class="text-left py-4 px-6 text-caption text-gray-400 uppercase tracking-[0.1em] font-semibold">
                        리스크 설명
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(risk, index) in categorizedOperationalRisks"
                      :key="`operational-${index}`"
                      class="border-b border-dark-600/50 hover:bg-dark-800/50 transition-colors"
                    >
                      <td class="py-4 px-6 text-white font-semibold text-base break-words whitespace-normal w-[40%]">
                        {{ risk.risk_name }}
                      </td>
                      <td class="py-4 px-6 text-gray-300 text-base leading-relaxed break-words whitespace-normal">
                        {{ risk.risk_description }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <!-- 카테고리 정보가 없는 경우 안내 -->
          <div v-if="categorizedBusinessRisks.length === 0 && categorizedMarketRisks.length === 0 && categorizedOperationalRisks.length === 0" class="bg-dark-700 border border-dark-600 rounded-2xl p-8 text-center">
            <i class="bi bi-info-circle text-gray-400 text-4xl mb-4"></i>
            <p class="text-gray-400 text-base">카테고리별 리스크 정보가 없습니다.</p>
          </div>
        </div>
      </section>

      <!-- 섹션 4: 웹 검증 결과 -->
      <section class="space-y-12">
        <div class="flex items-center gap-3 text-white">
          <i class="bi bi-4-square text-2xl"></i>
          <h4 class="text-heading-2 font-semibold">리스크별 검증 결과</h4>
        </div>

        <!-- 위험도별 탭 -->
        <div class="flex gap-3 border-b-2 border-dark-600">
          <button
            v-for="tab in ['전체', '높음', '중간', '낮음']"
            :key="tab"
            @click="selectedTab = tab"
            :class="[
              'px-6 py-3 text-base font-semibold transition-all duration-200',
              selectedTab === tab
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-400 hover:text-gray-300'
            ]"
          >
            {{ tab }}
            <span
              v-if="tab !== '전체'"
              :class="[
                'ml-2 px-2 py-0.5 rounded-full text-xs',
                tab === '높음' ? 'bg-red-500/20 text-red-400' :
                tab === '중간' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-emerald-500/20 text-emerald-400'
              ]"
            >
              {{ getTabCount(tab) }}
            </span>
          </button>
        </div>

        <div v-if="filteredWebVerifications.length > 0" class="space-y-10">
          <div
            v-for="(verification, verifyIndex) in filteredWebVerifications"
            :key="verification.risk_name"
            class="bg-dark-700 border border-dark-600 rounded-2xl p-8 space-y-8 shadow-[0_20px_45px_rgba(0,0,0,0.35)]"
          >
            <div class="flex items-center justify-between border-b-4 border-dark-600 pb-4">
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <i class="bi bi-search text-primary text-2xl"></i>
                </div>
                <div>
                  <p class="text-caption text-gray-400 uppercase tracking-[0.2em]">리스크 요소</p>
                  <div class="flex items-center gap-3">
                    <h5 class="text-white text-2xl font-semibold">{{ verification.risk_name }}</h5>
                    <span
                      :class="[
                        'px-3 py-1 rounded-full text-sm font-semibold',
                        getRiskCategory(verification.risk_name)?.includes('사업') ? 'bg-red-500/20 border border-red-500/40 text-red-400' :
                        getRiskCategory(verification.risk_name)?.includes('시장') ? 'bg-yellow-500/20 border border-yellow-500/40 text-yellow-400' :
                        getRiskCategory(verification.risk_name)?.includes('운영') ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-400' :
                        'bg-gray-500/20 border border-gray-500/40 text-gray-400'
                      ]"
                    >
                      {{ getRiskCategory(verification.risk_name) }}
                    </span>
                  </div>
                </div>
              </div>
              <span class="text-3xl font-semibold text-primary"># {{ verifyIndex + 1 }}</span>
            </div>

            <div class="bg-dark-800/60 border border-emerald-500/40 rounded-2xl p-6 space-y-6">
              <div class="flex items-start gap-3">
                <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-28 flex-shrink-0">원문 내용</p>
                <div class="flex-1 bg-dark-900/40 border-l-4 border-emerald-500/50 rounded-r-lg p-4">
                  <p class="text-gray-200 text-base leading-relaxed italic">
                    {{ getRiskSentence(verification.risk_name) }}
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-28 flex-shrink-0">리스크 설명</p>
                <div class="flex-1 bg-dark-900/40 border-l-4 border-emerald-500/50 rounded-r-lg p-4">
                  <p class="text-gray-200 text-base leading-relaxed">
                    {{ getRiskDescription(verification.risk_name) }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="verification.verification_points && verification.verification_points.length > 0" class="space-y-10">
              <div
                v-for="(point, pointIndex) in verification.verification_points"
                :key="pointIndex"
                class="bg-dark-800/60 border border-dark-600 rounded-2xl p-6"
              >
                <div class="flex items-start gap-4">
                  <div class="w-10 flex justify-center">
                    <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span class="text-primary font-semibold text-sm">{{ pointIndex + 1 }}</span>
                    </div>
                  </div>
                  <div class="flex-1 space-y-5">
                    <div class="flex items-start gap-3 mb-2">
                      <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">보도 제목</p>
                      <div class="flex-1">
                        <p class="text-primary text-base font-semibold leading-relaxed">
                          {{ point.source_title || '제목 정보 없음' }}
                        </p>
                      </div>
                    </div>

                    <div class="flex items-start gap-3 mb-2">
                      <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">보도 내용</p>
                      <div class="flex-1">
                        <p class="text-gray-200 text-base leading-relaxed">
                          {{ point.source_content || '내용 정보 없음' }}
                        </p>
                      </div>
                    </div>

                    <div v-if="point.source_description" class="flex items-start gap-3 mb-2">
                      <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">리스크 관련성</p>
                      <div class="flex-1">
                        <p class="text-gray-200 text-base leading-relaxed">
                          {{ point.source_description }}
                        </p>
                      </div>
                    </div>

                    <div v-if="point.source_url && point.source_url.length > 0" class="flex items-start gap-3 mb-2">
                      <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">자료 출처</p>
                      <div class="flex-1 rounded-xl border border-dark-600 px-4 py-3 bg-dark-900/40 flex flex-wrap items-center gap-4">
                        <a
                          v-if="point.source_url[0]"
                          :href="point.source_url[0]"
                          target="_blank"
                          class="text-gray-300 text-base no-underline break-all flex-1"
                        >
                          <i class="bi bi-link-45deg text-gray-300 text-base mr-2"></i>
                          {{ point.source_url[0] }}
                        </a>
                        <span v-else class="text-gray-500 text-base flex-1">URL 정보 없음</span>
                        <span class="text-gray-400 text-base whitespace-nowrap">
                          {{ point.source_date?.[0] || '-' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t-4 border-dark-600 my-6"></div>

            <div
              v-if="verification.implication && Array.isArray(verification.implication)"
              class="bg-dark-800/60 p-6 space-y-6"
            >
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <i class="bi bi-exclamation-triangle text-yellow-400 text-2xl"></i>
                </div>
                <h4 class="text-xl font-semibold text-white">위험도 종합평가</h4>
              </div>
              
              <div
                v-for="(implication, index) in verification.implication"
                :key="index"
                class="space-y-6"
              >
                <div v-if="implication.risk_confirmation" class="flex items-start gap-3">
                  <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">리스크 현실화</p>
                  <div class="flex-1 bg-dark-900/40 border-l-4 border-red-500/50 rounded-r-lg p-4">
                    <p class="text-gray-200 text-base leading-relaxed whitespace-pre-line">
                      {{ implication.risk_confirmation }}
                    </p>
                  </div>
                </div>
                
                <div v-if="implication.impact_range" class="flex items-start gap-3">
                  <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">리스크의 영향 범위</p>
                  <div class="flex-1 bg-dark-900/40 border-l-4 border-yellow-500/50 rounded-r-lg p-4">
                    <p class="text-gray-200 text-base leading-relaxed whitespace-pre-line">
                      {{ implication.impact_range }}
                    </p>
                  </div>
                </div>
                
                <div v-if="implication.mitigation_strategy" class="flex items-start gap-3">
                  <p class="text-caption text-gray-400 uppercase tracking-[0.1em] w-32 flex-shrink-0">리스크 완화 전략</p>
                  <div class="flex-1 bg-dark-900/40 border-l-4 border-emerald-500/50 rounded-r-lg p-4">
                    <p class="text-gray-200 text-base leading-relaxed whitespace-pre-line">
                      {{ implication.mitigation_strategy }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center py-12 text-gray-400">
          <i class="bi bi-inbox text-4xl mb-4"></i>
          <p class="text-base">선택한 위험도에 해당하는 검증 결과가 없습니다.</p>
        </div>
      </section>
    </div>

    <!-- 섹션 5: 액션 버튼 -->
    <div class="flex gap-20 border-t-4 border-dark-600 pt-20">
      <button
        @click="scrollToTop"
        class="flex-1 btn-secondary"
      >
        <i class="bi bi-arrow-up mr-2"></i> 맨 위로 올라가기
      </button>
      <button
        @click="handleReset"
        class="flex-1 btn-primary"
      >
        <i class="bi bi-arrow-clockwise mr-2"></i>다시 분석하기
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRiskAnalysisStore } from '@/stores/riskAnalysis';

const store = useRiskAnalysisStore();
const selectedTab = ref('전체');
const rootElement = ref(null);

const companyInfo = computed(() => {
  return store.analysisResults?.analysis_results?.step_1_company_info || {};
});

const riskFactors = computed(() => {
  return store.analysisResults?.analysis_results?.step_2_risk_factors?.risk_factors || [];
});

const businessRisks = computed(() => {
  const risks = store.analysisResults?.analysis_results?.step_2_risk_factors?.business_risks;
  return Array.isArray(risks) ? risks : [];
});

const marketRisks = computed(() => {
  const risks = store.analysisResults?.analysis_results?.step_2_risk_factors?.market_risks;
  return Array.isArray(risks) ? risks : [];
});

const operationalRisks = computed(() => {
  const risks = store.analysisResults?.analysis_results?.step_2_risk_factors?.operational_risks;
  return Array.isArray(risks) ? risks : [];
});

// 카테고리별 리스크 필터링
const categorizedBusinessRisks = computed(() => {
  return riskFactors.value.filter((r) => {
    if (!r.category) return false;
    const category = r.category.toString().trim();
    return category === '사업리스크' || category === '사업 리스크' || category.includes('사업');
  });
});

const categorizedMarketRisks = computed(() => {
  return riskFactors.value.filter((r) => {
    if (!r.category) return false;
    const category = r.category.toString().trim();
    return category === '시장리스크' || category === '시장 리스크' || category.includes('시장');
  });
});

const categorizedOperationalRisks = computed(() => {
  return riskFactors.value.filter((r) => {
    if (!r.category) return false;
    const category = r.category.toString().trim();
    return category === '운영리스크' || category === '운영 리스크' || category.includes('운영');
  });
});

const webVerifications = computed(() => {
  return store.analysisResults?.analysis_results?.step_3_web_verification?.web_verification_results || [];
});

// PDF 원문 가져오기
const getRiskSentence = (riskName) => {
  const risk = riskFactors.value.find((r) => r.risk_name === riskName);
  return risk?.sentence_from_pdf || '원문 정보 없음';
}

// 리스크 설명 가져오기
const getRiskDescription = (riskName) => {
  const risk = riskFactors.value.find((r) => r.risk_name === riskName);
  return risk?.risk_description || '리스크 정보 없음';
};

// 리스크 카테고리 가져오기
const getRiskCategory = (riskName) => {
  const risk = riskFactors.value.find((r) => r.risk_name === riskName);
  return risk?.category || '정보 없음';
};

// 리스크 레벨(위험도) 가져오기
const getRiskLevel = (riskName) => {
  if (!riskName) {
    return '정보 없음';
  }
  
  if (!riskFactors.value || riskFactors.value.length === 0) {
    return '정보 없음';
  }
  
  const risk = riskFactors.value.find((r) => r.risk_name === riskName);
  if (!risk) {
    return '정보 없음';
  }
  
  return risk.severity || '정보 없음';
};

// 위험도별로 필터링된 검증 결과
const filteredWebVerifications = computed(() => {
  if (!webVerifications.value || webVerifications.value.length === 0) {
    return [];
  }
  
  const currentTab = selectedTab.value;
  
  if (currentTab === '전체') {
    return webVerifications.value;
  }
  
  const filtered = webVerifications.value.filter((verification) => {
    if (!verification.risk_name) {
      return false;
    }
    
    const riskLevel = getRiskLevel(verification.risk_name);
    return riskLevel === currentTab;
  });
  
  return filtered;
});

// 탭별 개수 computed
const tabCounts = computed(() => {
  const counts = {
    '전체': webVerifications.value?.length || 0,
    '높음': 0,
    '중간': 0,
    '낮음': 0,
  };
  
  if (webVerifications.value && webVerifications.value.length > 0) {
    webVerifications.value.forEach((verification) => {
      if (verification.risk_name) {
        const riskLevel = getRiskLevel(verification.risk_name);
        if (riskLevel === '높음') counts['높음']++;
        else if (riskLevel === '중간') counts['중간']++;
        else if (riskLevel === '낮음') counts['낮음']++;
      }
    });
  }
  
  return counts;
});

// 탭별 개수 가져오기
const getTabCount = (tab) => {
  return tabCounts.value[tab] || 0;
};

// 카테고리별 심각도 매트릭스 데이터
const riskMatrix = computed(() => {
  const matrix = {
    '사업': { '높음': 0, '중간': 0, '낮음': 0 },
    '시장': { '높음': 0, '중간': 0, '낮음': 0 },
    '운영': { '높음': 0, '중간': 0, '낮음': 0 },
  };

  const categories = [
    { name: '사업', risks: categorizedBusinessRisks.value },
    { name: '시장', risks: categorizedMarketRisks.value },
    { name: '운영', risks: categorizedOperationalRisks.value },
  ];

  categories.forEach(({ name, risks }) => {
    risks.forEach((risk) => {
      const level = getRiskLevel(risk.risk_name);
      if (matrix[name][level] !== undefined) {
        matrix[name][level]++;
      }
    });
  });

  return matrix;
});

const handleReset = () => {
  store.reset();
};

const scrollToTop = () => {
  // rootElement를 통해 부모 요소 중 overflow-auto를 가진 요소 찾기
  if (rootElement.value) {
    let parent = rootElement.value.parentElement;
    while (parent && parent !== document.body) {
      // overflow-auto 클래스를 가진 요소 찾기
      if (parent.classList && parent.classList.contains('overflow-auto')) {
        parent.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // className 문자열에서도 확인
      const className = parent.className || '';
      if (typeof className === 'string' && className.includes('overflow-auto')) {
        if (parent.scrollTo) {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          parent.scrollTop = 0;
        }
        return;
      }
      parent = parent.parentElement;
    }
  }
  
  // fallback: 직접 overflow-auto 요소 찾기
  const overflowElements = document.querySelectorAll('.overflow-auto');
  if (overflowElements.length > 0) {
    const container = overflowElements[0];
    if (container.scrollTo) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      container.scrollTop = 0;
    }
    return;
  }
  
  // fallback: main 요소와 window 스크롤
  const mainElement = document.querySelector('main');
  if (mainElement && mainElement.scrollTo) {
    mainElement.scrollTo({ top: 0, behavior: 'smooth' });
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<style scoped>
</style>
